/**
 * A tiny JSON tokenizer for the code block's tinting: keys, strings, numbers,
 * booleans/null and punctuation. It only colours — anything it does not
 * recognise stays plain text, so broken JSON still shows in full.
 */
export type NxJsonTokenKind = 'key' | 'string' | 'number' | 'literal' | 'punct' | 'plain';

export interface NxJsonToken {
  kind: NxJsonTokenKind;
  text: string;
}

const TOKEN =
  /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b|([{}[\],:])/g;

export function tokenizeJson(source: string): NxJsonToken[] {
  const out: NxJsonToken[] = [];
  let last = 0;
  for (const m of source.matchAll(TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: 'plain', text: source.slice(last, at) });
    if (m[1] !== undefined) {
      out.push({ kind: m[2] ? 'key' : 'string', text: m[1] });
      if (m[2]) out.push({ kind: 'punct', text: m[2] });
    } else if (m[3] !== undefined) {
      out.push({ kind: 'number', text: m[3] });
    } else if (m[4] !== undefined) {
      out.push({ kind: 'literal', text: m[4] });
    } else {
      out.push({ kind: 'punct', text: m[5]! });
    }
    last = at + m[0].length;
  }
  if (last < source.length) out.push({ kind: 'plain', text: source.slice(last) });
  return out;
}

/** Pretty JSON with two-space indent; values JSON cannot hold fall back to `String`. */
export function formatJson(value: unknown): string {
  try {
    const text = JSON.stringify(value, null, 2);
    return text === undefined ? String(value) : text;
  } catch {
    return String(value);
  }
}
